// src/store/client/slices/modalSlice.ts
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { uniqueId } from 'lodash-es';

import type {
  CloseAllComponentModalByName,
  ComponentModal,
  IAlertModal,
  IBottomSheetModal,
  IConfirmModal,
  ILoadingModal,
  INotionModal,
  IOCRModal,
  IPaymentModal,
  OpenAlertModalPayload,
  OpenBottomSheetModalPayload,
  OpenComponentModalPayload,
  OpenConfirmModalPayload,
  OpenLoadingModalPayload,
  OpenNotionModalPayload,
  OpenOCRModalPayload,
  OpenPaymentModalPayload,
} from './modalSlice.types';

/**
 * 모달 슬라이스 상태 타입
 */
export interface ModalState {
  modal: (
    | ComponentModal
    | IAlertModal
    | IConfirmModal
    | ILoadingModal
    | IPaymentModal
    | INotionModal
    | IOCRModal
    | IBottomSheetModal
  )[];
  isModalOpen: boolean;
}

/**
 * 모달 슬라이스 초기 상태
 * @type {modal} - 모달 리스트
 * @type {isModalOpen} - 모달이 열려있는지 여부
 */
const initialState: ModalState = {
  modal: [],
  isModalOpen: false,
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    initializeModalState: () => initialState,
    openComponentModal(state, action: PayloadAction<OpenComponentModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_component_'),
        modalType: 'component',
        component: action.payload.component,
        closeType: action.payload.type || 'weak',
        isClosing: false,
        name: action.payload.name,
        motion: action.payload.motion || { type: 'dialog' },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openAlertModal(state, action: PayloadAction<OpenAlertModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_alert_'),
        modalType: 'alert',
        closeType: action.payload.type || 'weak',
        isClosing: false,
        alertType: action.payload.icon || 'none',
        title: action.payload.title,
        description: action.payload.description,
        onClose: action.payload.onClose,
        closeText: action.payload.closeText,
        motion: action.payload.motion || { type: 'dialog' },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openConfirmModal(state, action: PayloadAction<OpenConfirmModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_confirm_'),
        modalType: 'confirm',
        closeType: action.payload.type || 'weak',
        isClosing: false,
        alertType: action.payload.icon || 'none',
        title: action.payload.title,
        description: action.payload.description,
        onClose: action.payload.onClose,
        onConfirm: action.payload.onConfirm,
        closeText: action.payload.closeText,
        confirmText: action.payload.confirmText,
        motion: action.payload.motion || { type: 'dialog' },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openLoadingModal(state, action: PayloadAction<OpenLoadingModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_loading_'),
        modalType: 'loading',
        closeType: 'strong',
        isClosing: false,
        title: action.payload.title,
        description: action.payload.description,
        motion: action.payload.motion || { type: 'dialog' },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    closeLoadingModal(state) {
      state.modal = state.modal.filter((item) => item.modalType !== 'loading');
      state.isModalOpen = state.modal.length > 0;
    },
    openPaymentModal(state, action: PayloadAction<OpenPaymentModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_payment_'),
        modalType: 'payment',
        isClosing: false,
        startUrl: action.payload.startUrl,
        startParams: action.payload.startParams,
        paymentMethod: action.payload.paymentMethod,
        handleMessage: action.payload.handleMessage,
        motion: {
          type: 'payment',
        },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openNotionModal(state, action: PayloadAction<OpenNotionModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_notion_'),
        modalType: 'notion',
        isClosing: false,
        pageId: action.payload.pageId,
        motion: action.payload.motion || { type: 'dialog' },
        onClose: action.payload.onClose,
        closeText: action.payload.closeText,
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openOCRModal(state, action: PayloadAction<OpenOCRModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_ocr_'),
        modalType: 'ocr',
        isClosing: false,
        onClose: action.payload.onClose,
        onScanComplete: action.payload.onScanComplete,
        onScanCancel: action.payload.onScanCancel,
        originalsIdx: action.payload.originalsIdx,
        motion: action.payload.motion || { type: 'dialog' },
        disabledOverlayBlur: action.payload.disabledOverlayBlur ?? false,
      });
      state.isModalOpen = true;
    },
    openBottomSheetModal(state, action: PayloadAction<OpenBottomSheetModalPayload>) {
      state.modal.push({
        uuid: uniqueId('modal_bottomsheet_'),
        modalType: 'bottomSheet',
        component: action.payload.component,
        closeType: action.payload.type || 'weak',
        isClosing: false,
        name: action.payload.name,
        height: action.payload.height,
        motion: { type: 'bottomSheet' },
        disabledOverlayBlur: true,
      });
      state.isModalOpen = true;
    },
    closePaymentModal(state) {
      state.modal = state.modal.map((item) => {
        if (item.modalType === 'payment') {
          return { ...item, isClosing: true };
        }
        return item;
      });
    },
    closeOCRModal(state) {
      state.modal = state.modal.map((item) => {
        if (item.modalType === 'ocr') {
          return { ...item, isClosing: true };
        }
        return item;
      });
    },
    removeModalByUuid(state, action: PayloadAction<string>) {
      state.modal = state.modal.filter((item) => item.uuid !== action.payload);
      state.isModalOpen = state.modal.length > 0;
    },
    closeModal(state) {
      // 마지막의 모달 isClosing을 true로 변경
      state.modal = state.modal.map((item, index) => {
        if (index === state.modal.length - 1) {
          return { ...item, isClosing: true };
        }
        return item;
      });
    },
    closeModalByUuid(state, action: PayloadAction<string>) {
      state.modal = state.modal.map((item) => {
        if (item.uuid === action.payload) {
          return { ...item, isClosing: true };
        }
        return item;
      });
      state.isModalOpen = state.modal.length > 0;
    },
    closeAllComponentModalByName(state, action: PayloadAction<CloseAllComponentModalByName>) {
      state.modal = state.modal.map((item) => {
        if (item.modalType === 'component' && item.name === action.payload.name) {
          return { ...item, isClosing: true };
        }
        return item;
      });
      state.isModalOpen = state.modal.length > 0;
    },
  },
});

export const modalActions = modalSlice.actions;

export default modalSlice;
